/**
 * Hand-inlined Heroicons v2 (outline) for the Report Queue. The project carries
 * no icon package — icons are local <svg> paths. Sizes: w-3.5 toolbar, w-4
 * chrome, w-5 headers. All decorative (aria-hidden); the surrounding control
 * carries the accessible name.
 */

const base = (cls) => ({
  className: cls,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  'aria-hidden': 'true',
})

export const CheckIcon = ({ className = 'w-3 h-3' }) => (
  <svg {...base(className)} strokeWidth={2.6}><path strokeLinecap="round" d="M5 13l4 4L19 7" /></svg>
)

export const XIcon = ({ className = 'w-3 h-3' }) => (
  <svg {...base(className)} strokeWidth={2.6}><path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" /></svg>
)

export const ClockIcon = ({ className = 'w-3 h-3' }) => (
  <svg {...base(className)} strokeWidth={2}><circle cx="12" cy="12" r="9" /><path strokeLinecap="round" d="M12 7v5l3 2" /></svg>
)

export const RefreshIcon = ({ className = 'w-3 h-3' }) => (
  <svg {...base(className)} strokeWidth={1.8}><path strokeLinecap="round" d="M4 4v6h6M20 8a8 8 0 00-14.9-2M4 16a8 8 0 0014.9 2M20 20v-6h-6" /></svg>
)

export const BoltIcon = ({ className = 'w-4 h-4' }) => (
  <svg {...base(className)} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" /></svg>
)

export const CalendarIcon = ({ className = 'w-4 h-4' }) => (
  <svg {...base(className)} strokeWidth={1.8}><rect x="3" y="5" width="18" height="16" rx="2" /><path strokeLinecap="round" d="M8 3v4M16 3v4M3 11h18" /></svg>
)

export const InfoIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg {...base(className)} strokeWidth={1.8}><circle cx="12" cy="12" r="9" /><path strokeLinecap="round" d="M12 16v-5M12 8h.01" /></svg>
)

export const WarningIcon = ({ className = 'w-4 h-4' }) => (
  <svg {...base(className)} strokeWidth={1.8}><path strokeLinecap="round" d="M12 9v4M12 17h.01M10.3 3.9L2.4 18a2 2 0 001.7 3h15.8a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" /></svg>
)

export const ArrowRightIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg {...base(className)} strokeWidth={2.2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 6l6 6-6 6" /></svg>
)

export const ChevronLeftIcon = ({ className = 'w-4 h-4' }) => (
  <svg {...base(className)} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M15 18l-6-6 6-6" /></svg>
)

export const ChevronDownIcon = ({ className = 'w-3 h-3' }) => (
  <svg {...base(className)} strokeWidth={2.4}><path strokeLinecap="round" strokeLinejoin="round" d="M6 9l6 6 6-6" /></svg>
)

export const PencilIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg {...base(className)} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M4 20h4l10-10-4-4L4 16v4z" /></svg>
)

export const TrashIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg {...base(className)} strokeWidth={1.8}><path strokeLinecap="round" d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" /></svg>
)

export const DuplicateIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg {...base(className)} strokeWidth={1.8}><rect x="8" y="8" width="12" height="12" rx="2" /><path strokeLinecap="round" d="M16 8V6a2 2 0 00-2-2H6a2 2 0 00-2 2v8a2 2 0 002 2h2" /></svg>
)

/** "Skip the next run" — a plus, matching design screen 6's kebab. */
export const SkipIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg {...base(className)} strokeWidth={1.8}><path strokeLinecap="round" d="M5 12h14M12 5v14" /></svg>
)

/** "View run history" — an arrow into a baseline, per design screen 6's kebab. */
export const HistoryIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg {...base(className)} strokeWidth={1.8}><path strokeLinecap="round" d="M12 3v12M8 11l4 4 4-4M4 19h16" /></svg>
)

/** Pause bars — the kebab's Pause item. */
export const PauseIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg {...base(className)} strokeWidth={1.8}><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
)

/** Play triangle — the kebab's Resume item. */
export const PlayIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg {...base(className)} strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M7 4l12 8-12 8V4z" /></svg>
)

export const SlidersIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg {...base(className)} strokeWidth={1.8}><path strokeLinecap="round" d="M11 4h9M11 12h9M11 20h9M4 6l2 2 3-3" /></svg>
)

export const DownloadIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg {...base(className)} strokeWidth={1.8}><path strokeLinecap="round" d="M12 3v12M8 11l4 4 4-4M4 19h16" /></svg>
)

/** Indeterminate spinner. */
export const Spinner = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={`${className} animate-spin`} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
    <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
)

// spec 092 — the mock's Column Filter control. Heroicons v2 outline "funnel",
// matching the hand-inlined set above (this repo carries no icon package).
export const FunnelIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 01-.659 1.591l-5.432 5.432a2.25 2.25 0 00-.659 1.591v2.927a2.25 2.25 0 01-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 00-.659-1.591L3.659 7.409A2.25 2.25 0 013 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0112 3z" />
  </svg>
)
